import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
  Button,
  ButtonGroup
} from "@mui/material";
import { styled } from "@mui/system";
import { ReactComponent as ArrowDropUpIcon } from "../icons/ShortIcon.svg";
import { ReactComponent as ArrowDropDownIcon } from "../icons/ShortIconDown.svg";
import newExcel from "../../../../assets/images/FileXls.svg";
import { orHeatMapWeekAllLosses, orHeatMapWeeklyTrendLossTree } from "Samarth Module/api/orltAPI";
import { SelectedAlertsContext } from "utils/SelectedAlertContext";
import CustomButton from "Samarth Module/components/buttons/CustomButton";

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "Unilever Shilling",
  fontSize: "10px",
  textAlign: "center",
  whiteSpace: "normal",
  lineHeight: 1.1,
  padding: "4px 6px",
  border: "none",
  minWidth: "70px",
  maxWidth: "90px",
  "&.MuiTableCell-head": {
    fontWeight: 700,
    backgroundColor: "#fff",
    color: "#000",
  },
  "&.MuiTableCell-body": {
    fontWeight: 400,
  },
}));

const StickyFirstColumnHead = styled(StyledTableCell)(() => ({
  position: "sticky",
  left: 0,
  backgroundColor: "#fff",
  zIndex: 5,
  fontWeight: 700,
  color: "#354ACD",
}));

const StickyFirstColumnCell = styled(StyledTableCell)(() => ({
  position: "sticky",
  left: 0,
  backgroundColor: "#fff",
  zIndex: 4,
  fontWeight: 700,
  color: "#354ACD",
}));

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: "#fff !important",
}));

const getBGColor = (value, show) => {
  if (!show) {
    return { backgroundColor: "#e1dadac0" }
  }
  if (value === Infinity || value >= 5) return { backgroundColor: "#FCA5A5" };
  if (value >= 3) return { backgroundColor: "#FFEC9A" };
  return { backgroundColor: "#A3E1D2" };
};

const HeatMapTable = ({ tblView, exportHeatmap }) => {
  const map_losses = localStorage.getItem("map_losses");
  const map_losses_filter = localStorage.getItem("map_losses_filter");

  const [heatMapData, setHeatMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lossSelection, setLossSelection] = useState(map_losses || "Loss 1");
  const [groupingSelection, setGroupingSelection] = useState(map_losses_filter || "CalendarWeek");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dynamicKeys, setDynamicKeys] = useState([]);
  const [viewType, setViewType] = useState("Week");
  const { orLossTreeSelectedFilters } = useContext(SelectedAlertsContext);

  const transformData = (data, grouping) => {
    const keysSet = new Set();
    const transformed = data.map((row) => {
      const label = row[grouping] || "N/A";
      const values = Object.entries(row)
        .filter(([key]) => key !== grouping)
        .map(([key, value]) => {
          if (key !== 'display') keysSet.add(key);
          return { key, label: key, value: value ?? 0, display: row?.display ?? true };
        });

      return { label, values };
    });

    const keys = Array.from(keysSet);
    const sortedKeys = keys.filter((k) => k !== "Total");
    if (keys.includes("Total")) sortedKeys.push("Total");
    setDynamicKeys(sortedKeys);

    // Move Total row to the top
    const totalRow = transformed.find((r) => r.label === "Total");
    const rest = transformed.filter((r) => r.label !== "Total");
    return totalRow ? [totalRow, ...rest] : [...rest];
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setHeatMapData([])

        let response = {}
        if (groupingSelection === "CalendarWeek") {
          response = await orHeatMapWeeklyTrendLossTree(
            groupingSelection,
            lossSelection,
            orLossTreeSelectedFilters,
            tblView,
            viewType
          );

        } else {
          response = await orHeatMapWeekAllLosses(
            groupingSelection,
            lossSelection,
            orLossTreeSelectedFilters,
            tblView,
            viewType
          );
        }

        if (isMounted) {
          const lossKey = lossSelection === "Loss 1" ? "Loss1" : "Loss2";
          const transformed = transformData(
            response?.[lossKey] || [],
            groupingSelection
          );
          setHeatMapData(transformed);
          setSortColumn(null);
        }
      } catch (err) {
        console.error("Heat map error", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupingSelection, lossSelection, JSON.stringify(orLossTreeSelectedFilters), tblView, viewType]);


  // useEffect(() => {
  //   const map_losses = localStorage.getItem("map_losses");
  //   const map_losses_filter = localStorage.getItem("map_losses_filter");
  //   if (map_losses) setLossSelection(map_losses);
  //   if (map_losses_filter) setGroupingSelection(map_losses_filter)
  // }, [])

  const handleSort = (key) => {
    const order = sortColumn === key && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(key);
    setSortOrder(order);

    // Separate the Total row from other rows
    const totalRow = heatMapData.find(row => row.label === "Total");
    const dataRows = heatMapData.filter(row => row.label !== "Total");

    const sorted = [...dataRows].sort((a, b) => {
      const valA = a.values.find((v) => v.key === key)?.value ?? 0;
      const valB = b.values.find((v) => v.key === key)?.value ?? 0;
      
      // Handle Infinity values - always put them at the end for desc, start for asc
      if (valA === Infinity && valB === Infinity) return 0;
      if (valA === Infinity) return order === "asc" ? 1 : -1;
      if (valB === Infinity) return order === "asc" ? -1 : 1;
      
      // Normal numerical comparison
      return order === "asc" ? valA - valB : valB - valA;
    });

    // Always put Total row at the top
    setHeatMapData(totalRow ? [totalRow, ...sorted] : sorted);
  };

  const chartHeight = () => {
    const mediaQuery1 = window.matchMedia("(max-width: 1300px)").matches;
    const mediaQuery2 = window.matchMedia("(max-width: 1450px)").matches;
    const mediaQuery3 = window.matchMedia("(max-width: 1550px)").matches;
    return mediaQuery1 ? 216 : mediaQuery2 ? 240 : mediaQuery3 ? 275 : 300
  }

  const setLossSelectionLoss = (val) => {
    setLossSelection(val)
    localStorage.setItem("map_losses", val)
  }

  const setGroupingSelectionLoss = (val) => {
    setGroupingSelection(val)
    localStorage.setItem("map_losses_filter", val)
  }

  const isNoData = useMemo(() => {
    if (!Array.isArray(heatMapData)) return;
    return (heatMapData.length === 1 && !(heatMapData[0]?.values.find((obj) => obj.value !== 0))) || heatMapData.length === 0
  }, [heatMapData])

  //  const filterConbination = useMemo(() => {
  //     const labels = orLossTreeSelectedFilters.map((o) => o.label);
  //     if (labels.length === 0) return '';
  //     if (labels.length === 1) return labels[0];
  //     return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  //   }, [orLossTreeSelectedFilters]);

  
  const handleExportToExcel = () => {
    if (exportHeatmap) {
      exportHeatmap(groupingSelection);
    }
  }

  return (
    <Box sx={{
      p: 0,
      borderRadius: 2,
      width: "100%",
      backgroundColor: "#fff",
      display: "flex",
      flexDirection: "column",
      height: "49vh" //"calc(37vh + 110px)", // Total height = table + header + dropdown + footer
    }}>
      {/* Header and Dropdown section - fixed height */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0,
        height: "25px",
        p: 1,
        pt: 0
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>
          Heat Map
        </Typography>
        <Box sx={{ display: "flex", gap: '8px' }}>
          <Select
            size='small'
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            sx={{ fontSize: "12px", height: "30px", minWidth: "auto" }}
            title={viewType}
          >
            <MenuItem value='Week' sx={{ padding: '5px', fontSize: '12px' }} >W</MenuItem>
            <MenuItem value='Month' sx={{ padding: '5px', fontSize: '12px' }} >M</MenuItem>
          </Select>
          <Select
            size='small'
            value={lossSelection}
            onChange={(e) => setLossSelectionLoss(e.target.value)}
            sx={{ fontSize: "12px", height: "30px", minWidth: "90px" }}
          >
            <MenuItem value='Loss 1' sx={{ padding: '5px', fontSize: '12px' }} >Loss 1</MenuItem>
            <MenuItem value='Loss 2' sx={{ padding: '5px', fontSize: '12px' }} >Loss 2</MenuItem>
          </Select>
          {groupingSelection !== "CalendarWeek" && <Select
            size='small'
            value={groupingSelection}
            onChange={(e) => setGroupingSelectionLoss(e.target.value)}
            sx={{ fontSize: "11px", height: "28px", minWidth: "90px" }}
          >
            <MenuItem value='Factory' sx={{ padding: '5px', fontSize: '12px' }} >Factory</MenuItem>
            <MenuItem value='BG' sx={{ padding: '5px', fontSize: '12px' }} >BG</MenuItem>
            <MenuItem value='SmallC' sx={{ padding: '5px', fontSize: '12px' }} >SmallC</MenuItem>
            <MenuItem value='Format' sx={{ padding: '5px', fontSize: '12px' }} >Format</MenuItem>
            <MenuItem value='Basepack' sx={{ padding: '5px', fontSize: '12px' }} >Basepack</MenuItem>
            <MenuItem value='CBU' sx={{ padding: '5px', fontSize: '12px' }} >CBU</MenuItem>
            <MenuItem value='Site Code' sx={{ padding: '5px', fontSize: '12px' }} >Site Code</MenuItem>
          </Select>}
          <ButtonGroup size="small" sx={{ height: "30px" }}>
            <Button
              variant={groupingSelection === "Weekly Trend" ? "contained" : "outlined"}
              sx={groupingSelection === "CalendarWeek"
                ? { background: '#1976d2', color: '#fff' }
                : { background: '#fff', color: '#1976d2', border: '1px solid #1976d2' }}
              onClick={() => {
                setGroupingSelectionLoss("CalendarWeek");
              }}
            >
              OR loss trend
            </Button>

            <Button
              variant={groupingSelection === "Factory View" ? "contained" : "outlined"}
              sx={groupingSelection !== "CalendarWeek"
                ? { background: '#1976d2', color: '#fff' }
                : { background: '#fff', color: '#1976d2', border: '1px solid #1976d2' }}
              onClick={() => { setGroupingSelectionLoss("Factory"); }}
            >
              Loss Details
            </Button>
          </ButtonGroup>
          <div style={{ display: 'flex', columnGap: '10px' }}>
            <CustomButton icon={newExcel} label="Export" type="mm-save" onClick={handleExportToExcel} />
          </div>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{
        borderBottom: "1px solid #EAECF0",
        flexShrink: 0
      }} />

      {/* Table container - exactly 37vh */}
      <Box sx={{
        height: "37vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : isNoData ? <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "290px",
            marginTop: '10px'
          }}
        >
          Data is not available
        </Box> : (
          <Box
            sx={{ maxWidth: "100%", maxHeight: chartHeight(), overflow: "auto" }}>
            <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <StickyFirstColumnHead>
                      {groupingSelection}
                    </StickyFirstColumnHead>
                    {dynamicKeys.map((key, idx) => (
                      <StyledTableCell key={idx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.2,
                          }}
                        >
                          <Box>{key}</Box>
                          <IconButton
                            size='small'
                            onClick={() => handleSort(key)}
                            sx={{ padding: 0, marginLeft: "2px", color: "#000" }}
                          >
                            {sortColumn === key ? (
                              sortOrder === "asc" ? (
                                <ArrowDropUpIcon />
                              ) : (
                                <ArrowDropDownIcon fontSize='small' />
                              )
                            ) : (
                              <ArrowDropUpIcon
                                fontSize='small'
                                sx={{ opacity: 0.3 }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {heatMapData.map((row, idx) => {
                    const isTotalRow = row.label === "Total";
                    const stickyStyle = isTotalRow
                      ? {
                          position: "sticky",
                          top: 33,
                          zIndex: 3,
                          backgroundColor: "#f0f4ff",
                        }
                      : {};
                    return (
                      <StyledTableRow key={idx}>
                        <StickyFirstColumnCell
                          sx={{
                            ...(isTotalRow
                              ? {
                                  position: "sticky",
                                  top: 33,
                                  left: 0,
                                  zIndex: 6,
                                  backgroundColor: "#f0f4ff",
                                  fontWeight: 700,
                                }
                              : {}),
                          }}
                        >
                          {row.label}
                        </StickyFirstColumnCell>
                        {dynamicKeys.map((key, colIdx) => {
                          const valObj = row.values.find((v) => v.key === key);
                          const val = valObj?.value ?? 0;
                          return (
                            <StyledTableCell key={colIdx} sx={stickyStyle}>
                                <div
                                  style={{
                                    ...getBGColor(val, valObj?.display),
                                    borderRadius: "3px",
                                    padding: "1px 0",
                                    fontSize: "10px",
                                    color: !valObj?.display ? "#e1dadac0" : "#000",
                                  }}
                                >
                                  {val === Infinity
                                    ? "∞"
                                    : typeof val === "number"
                                      ? `${Math.round(val)}%`
                                      : "0%"}
                                </div>
                            </StyledTableCell>
                          );
                        })}
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
        )}
      </Box>

      {/* Footer section - fixed height */}
      {!isNoData && <Box sx={{
        borderTop: "1px solid #EAECF0",
        pt: 0.5,
        pb: 0,
        height: "28px",
        flexShrink: 0
      }}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <LegendItem label='High' color='#FCA5A5' />
          <LegendItem label='Medium' color='#FFEC9A' />
          <LegendItem label='Low' color='#A3E1D2' />
        </Box>
      </Box>}
    </Box>
  );
};

const LegendItem = ({ label, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", pt: "4px" }}>
    <Box
      component='span'
      sx={{
        width: "2px",
        height: "12px",
        backgroundColor: color,
        display: "inline-block",
        borderRadius: "1px",
      }}
    />
    <Typography sx={{ fontSize: "11px", color: "#000" }}>{label}</Typography>
  </Box>
);

export default HeatMapTable;